import { useState, useMemo, useEffect, useCallback } from 'react';
import Agriculture from '@mui/icons-material/Agriculture';
import { Button } from '@practium/ui';
import { SelectionModal } from '../SelectionModal/SelectionModal';
import { SelectionCard } from '../../Cards/SelectionCard/SelectionCard';
import { LocationIcon, FarmIcon } from '../../Icons';
import type { PropDTO } from '../../../types';
import { fuzzyFilter } from '@practium/shared';
import { arraysEqual } from '../../../utils/parcellingFormatters';
import gridStyles from '../SelectionModal/SelectionGrid.module.css';

export interface FarmSelectionModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSave: (farmIds: string[]) => void;
	farms: PropDTO[];
	selectedFarmIds?: string[];
	showAllOption?: boolean;
	loading?: boolean;
}

export const FarmSelectionModal = ({
	isOpen,
	onClose,
	onSave,
	farms,
	selectedFarmIds = [],
	showAllOption = false,
	loading = false,
}: FarmSelectionModalProps) => {
	const [search, setSearch] = useState('');
	const initialRealIds = selectedFarmIds.filter((id) => id !== 'all');
	const initialIsAll = selectedFarmIds.includes('all');
	const [pendingIds, setPendingIds] = useState<string[]>(initialRealIds);
	const [isAll, setIsAll] = useState(initialIsAll);

	useEffect(() => {
		if (isOpen) {
			setPendingIds(selectedFarmIds.filter((id) => id !== 'all'));
			setIsAll(selectedFarmIds.includes('all'));
			setSearch('');
		}
	}, [isOpen, selectedFarmIds]);

	const filteredFarms = useMemo(() => {
		if (!search.trim()) return farms;
		return fuzzyFilter(farms, search, (f) => `${f.name} ${f.city} ${f.state}`);
	}, [farms, search]);

	const toggleFarm = useCallback((farmId: string) => {
		setIsAll(false);
		setPendingIds((prev) =>
			prev.includes(farmId) ? prev.filter((id) => id !== farmId) : [...prev, farmId]
		);
	}, []);

	const handleAllSelect = useCallback(() => {
		setIsAll(true);
		setPendingIds([]);
	}, []);

	const handleSave = () => {
		onSave(isAll ? ['all'] : pendingIds);
		onClose();
	};

	const wasAll = selectedFarmIds.includes('all');
	const originalRealIds = selectedFarmIds.filter((id) => id !== 'all');
	const hasChanges = isAll !== wasAll || (!isAll && !arraysEqual(pendingIds, originalRealIds));

	return (
		<SelectionModal
			isOpen={isOpen}
			title="Selecione as fazendas para este parcelamento"
			searchPlaceholder="Buscar por nome, cidade ou estado..."
			searchValue={search}
			onSearchChange={setSearch}
			onClose={onClose}
			footer={
				hasChanges ? (
					<Button
						variant="primary"
						onClick={handleSave}
						disabled={loading}
						className={gridStyles.saveButton}
					>
						Salvar Alterações
					</Button>
				) : undefined
			}
		>
			<div className={gridStyles.grid}>
				{showAllOption && (
					<SelectionCard
						name="Todas as Fazendas"
						location="Centro - Sudeste"
						icon={
							<div className={gridStyles.iconWrapper}>
								<svg
									width="70"
									height="70"
									viewBox="0 0 70 70"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
									className={gridStyles.allIcon}
								>
									<path
										fillRule="evenodd"
										clipRule="evenodd"
										d="M11.2346 15.8817C9.77242 16.0024 8.20877 16.4937 5.17812 17.7846C2.86375 18.7705 1.4777 19.4558 1.25512 19.724C1.16557 19.8319 1.05305 20.2062 1.00506 20.5558L0.917969 21.1914H34.9112H68.9044V20.8024C68.9044 20.265 68.6197 20.0504 66.791 19.2087C65.9157 18.8059 64.2335 17.9517 63.0529 17.3102C60.4068 15.8726 59.6451 15.6617 57.4884 15.7686C55.5963 15.8624 54.7254 16.0993 52.8399 17.0331C51.126 17.8819 49.0078 18.4754 46.7589 18.7368C44.6903 18.9772 43.4699 18.8526 41.1505 18.1639C39.2819 17.6091 39.0737 17.5756 37.5274 17.5827C36.0179 17.5895 35.7283 17.636 33.9044 18.1643C30.4204 19.1736 30.1443 19.2162 28.2943 19.0312C26.145 18.8165 23.9253 18.3328 21.2579 17.4984C16.6357 16.0523 14.107 15.6443 11.2346 15.8817ZM0.965 23.7228C0.977578 24.5134 1.02666 25.1843 1.0741 25.2135C1.12141 25.2428 1.9908 25.054 3.00594 24.7941C4.02107 24.534 6.96805 23.895 9.55463 23.3741C12.1412 22.8531 14.2895 22.3949 14.3284 22.356C14.3673 22.317 11.3713 22.2852 7.67051 22.2852H0.942031L0.965 23.7228ZM20.2325 22.9179C17.8053 23.5346 7.21031 26.31 3.21102 27.3768L1.02352 27.9603L0.987012 31.4152C0.958164 34.1463 0.987559 34.8843 1.12783 34.9381C1.22531 34.9755 2.81057 34.385 4.65066 33.626C10.0098 31.4151 16.2195 28.9212 17.7564 28.3626C18.5377 28.0786 20.2912 27.2944 21.6529 26.62C23.0147 25.9455 25.8072 24.7335 27.8584 23.9267C29.9097 23.1198 31.6272 22.4204 31.6752 22.3724C31.7232 22.3244 29.7066 22.2908 27.1938 22.2976L22.6251 22.31L20.2325 22.9179ZM33.4218 23.224C30.2467 24.4941 27.3722 25.8515 22.8725 28.2052C20.7813 29.2991 17.9937 30.6537 16.6778 31.2155C14.2645 32.2457 11.1254 33.6854 4.54402 36.7802L0.955156 38.4679V44.0485C0.955156 47.8198 1.00123 49.6289 1.09734 49.6289C1.17555 49.6289 3.59027 48.2254 6.46355 46.5099C9.3367 44.7945 12.3336 43.031 13.1231 42.5909C14.7029 41.7104 16.235 40.6097 20.6257 37.2006C25.3528 33.5305 25.5686 33.3685 27.03 32.397C27.7984 31.8861 28.767 31.341 29.1825 31.1855C30.2973 30.7684 30.8594 30.3706 32.71 28.6884C33.6301 27.8521 35.1212 26.6745 36.0235 26.0716C37.959 24.7781 41.2872 22.4189 41.2872 22.3401C41.2872 22.3099 40.0413 22.2868 38.5186 22.2888L35.7501 22.2925L33.4218 23.224ZM40.0342 25.379C37.9035 27.0806 35.2066 29.2841 34.0411 30.2755C31.6346 32.3226 16.8834 44.142 12.8497 47.2552C11.3834 48.3868 8.10664 50.8628 5.56805 52.7574L0.952422 56.2021L0.987969 62.5883L1.02352 68.9746L5.74031 69.0014L10.4571 69.0282L11.6192 67.4297C12.2584 66.5506 13.4864 64.9389 14.348 63.8482C16.5151 61.1054 17.6344 59.5932 19.2062 57.2852C20.9479 54.7274 22.2034 53.0223 23.5345 51.4062C24.123 50.6919 25.4141 48.9385 26.4038 47.5098C28.6432 44.2772 29.4953 43.1822 31.514 40.9432C32.3932 39.9678 34.6049 37.3857 36.4287 35.2051C38.2527 33.0244 40.7824 30.102 42.0504 28.7109C43.3184 27.3198 45.1112 25.3522 46.0345 24.3384C46.9576 23.3246 47.713 22.448 47.713 22.3902C47.713 22.3325 46.8568 22.2852 45.8105 22.2852H43.9082L40.0342 25.379ZM49.0431 23.3793C48.1754 24.8217 47.1739 26.0437 44.854 28.4911C43.7926 29.6108 42.6994 30.9028 42.4248 31.3622C41.6518 32.6554 41.1267 33.3432 38.5513 36.4358C35.462 40.1455 35.0022 40.75 34.1639 42.2028C33.3399 43.6306 32.92 44.2351 30.0016 48.1934C27.2814 51.883 22.0101 60.0547 19.1904 64.9529C18.0373 66.9561 17.055 68.6964 17.0076 68.82C16.928 69.0274 17.7471 69.042 27.6876 69.0096L38.454 68.9746L38.903 67.4023C39.1499 66.5376 39.604 65.1295 39.9121 64.2733C40.2596 63.3071 40.5739 62.0822 40.7403 61.0446C41.4599 56.5597 43.1426 49.6055 44.5844 45.1583C44.8819 44.241 45.58 42.4189 46.1358 41.1091C47.7186 37.379 50.4586 29.6613 51.5262 25.925C52.0608 24.0543 52.4981 22.4701 52.4981 22.4045C52.4981 22.3389 51.8674 22.2873 51.0968 22.2899L49.6954 22.2949L49.0431 23.3793ZM54.1465 22.7979C54.0685 23.0799 53.478 25.3101 52.8345 27.7539C51.6263 32.3411 51.1442 34.0148 49.1526 40.5371C47.1005 47.2577 45.9963 52.1441 45.5922 56.2956C45.1422 60.9164 44.9436 62.5138 44.4826 65.2148C44.2195 66.7563 43.9693 68.2484 43.9265 68.5303L43.8489 69.043H54.2574C63.5532 69.043 64.6661 69.0204 64.6661 68.8317C64.6661 68.7155 64.4155 67.6237 64.1091 66.4053C63.0188 62.0684 62.6854 60.0042 62.4815 56.3281C62.2295 51.7865 61.9213 49.6621 60.9249 45.5957C60.2337 42.7749 60.073 41.912 59.3975 37.3926C59.1279 35.5879 58.6985 33.1193 58.4432 31.9069C57.9871 29.7392 57.7195 27.8564 57.4256 24.7461C57.344 23.8813 57.245 22.9738 57.2056 22.7295L57.1341 22.2852H55.7113H54.2883L54.1465 22.7979ZM58.9239 22.4675C58.9239 23.335 62.9462 42.8023 64.2525 48.2575C64.6222 49.8013 65.1733 52.1719 65.4772 53.5254C65.8308 55.1003 66.535 57.4984 67.4329 60.1859L68.836 64.3854L68.8707 48.8689L68.9056 33.3523L68.1658 32.3646C67.1343 30.9874 66.1163 29.2708 64.8732 26.813C64.2882 25.6564 63.4446 24.1644 62.9987 23.4976L62.1877 22.2852H60.5558C59.2866 22.2852 58.9239 22.3258 58.9239 22.4675ZM64.6141 22.4561C64.6696 22.5501 64.9702 22.9346 65.282 23.3105C65.5939 23.6865 66.3301 24.6605 66.9183 25.4749C67.5063 26.2894 68.1937 27.2027 68.446 27.5045L68.9044 28.0532V25.1692V22.2852H66.7088C64.9897 22.2852 64.5351 22.3222 64.6141 22.4561Z"
										fill="currentColor"
									/>
								</svg>
							</div>
						}
						locationIcon={<LocationIcon  />}
						farmInfo={`${farms.length} fazendas`}
						farmIcon={<FarmIcon />}
						selected={isAll}
						onClick={handleAllSelect}
					/>
				)}
				{filteredFarms.map((farm) => (
					<SelectionCard
						key={farm._id}
						name={farm.name}
						location={`${farm.city}, ${farm.state}`}
						icon={
							<div className={gridStyles.iconWrapper}>
								<Agriculture className={gridStyles.farmIcon} />
							</div>
						}
						locationIcon={<LocationIcon />}
						farmInfo={farm.area ? `${farm.area} hectares` : undefined}
						farmIcon={<FarmIcon />}
						selected={pendingIds.includes(farm._id)}
						onClick={() => toggleFarm(farm._id)}
					/>
				))}
				{filteredFarms.length === 0 && !showAllOption && (
					<p className={gridStyles.empty}>Nenhuma fazenda encontrada</p>
				)}
			</div>
		</SelectionModal>
	);
};
